import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

/**
 * Where the provisioning agent packages live (epic &167 #143).
 *
 * The package pages of the `provision-agent` build in rr/releases: every published build lands
 * there, and the topmost file is the current one. A direct per-file download URL does not exist
 * on those pages — so each button opens the package page of its own platform build and the
 * operator picks the topmost file. When the LS-side download endpoint (#281 follow-up) lands,
 * these constants will point at it.
 *
 * TODO(#281 follow-up): the package ids rotate with every build — the next phase resolves the
 * link dynamically (config / attestation registry) instead of pinning the ids here.
 */
export const AGENT_WINDOWS_PACKAGE_URL = 'https://gitlab.onesun.ae/rr/releases/-/packages/777';
export const AGENT_LINUX_PACKAGE_URL = 'https://gitlab.onesun.ae/rr/releases/-/packages/764';

export interface FactoryUsbDownloadCardProps {
  /** Package page the Windows link opens; the rr/releases canon is the default. */
  windowsUrl?: string;
  /** Package page the Linux link opens; the rr/releases canon is the default. */
  linuxUrl?: string;
  /** Build the links point at, when the caller knows it — the version is not part of the base. */
  version?: string;
}

/**
 * The step before the wizard: an administrator who has never issued a medium needs the agent
 * first. Downloading is all this card does — it does not probe the machine, so it stays honest
 * about an agent that is not installed yet.
 */
export function FactoryUsbDownloadCard({
  windowsUrl = AGENT_WINDOWS_PACKAGE_URL,
  linuxUrl = AGENT_LINUX_PACKAGE_URL,
  version,
}: FactoryUsbDownloadCardProps) {
  const { t } = useTranslation();

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border p-4"
      aria-label={t('factoryUsb.downloadTitle')}
    >
      <div className="flex flex-col gap-1">
        <p className="font-medium">{t('factoryUsb.downloadTitle')}</p>
        <p className="text-sm text-muted-foreground">{t('factoryUsb.downloadDescription')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline" size="sm">
          <a href={windowsUrl} target="_blank" rel="noreferrer">
            {t('factoryUsb.downloadWindows')}
          </a>
        </Button>
        <Button asChild variant="outline" size="sm">
          <a href={linuxUrl} target="_blank" rel="noreferrer">
            {t('factoryUsb.downloadLinux')}
          </a>
        </Button>
        {version ? (
          <Badge variant="secondary">{t('factoryUsb.downloadVersion', { version })}</Badge>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground">{t('factoryUsb.downloadHint')}</p>
    </section>
  );
}
